import { useOnboarding, PRICING } from "./OnboardingContext";
import { Check, ArrowLeft, ArrowRight, Star, Users, TrendingUp, Crown, BookOpen } from "lucide-react";
import { useState } from "react";
import BrochureModal from "./BrochureModal";

const tierIcons = {
  essentielle: Users,
  performance: TrendingUp,
  signature: Crown
};

const StepDistribution = () => {
  const { selectedDistributionTier, setDistributionTier, nextStep, prevStep, monthlyPrice, getPackageDetails } = useOnboarding();
  const [showBrochure, setShowBrochure] = useState(false);
  const pkg = getPackageDetails();

  const tiers = Object.values(PRICING.distributionTiers);

  return (
    <div data-testid="step-distribution" className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <p className="font-body text-sm tracking-[0.15em] uppercase text-[#B89F7C] mb-2">
          Étape 2
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-[#0A0A0A] mb-4">
          Choisissez votre niveau de diffusion
        </h1>
        <p className="font-body text-[#525252] max-w-2xl">
          Sélectionnez le niveau de visibilité qui correspond à vos ambitions. 
          Notre réseau de salles partenaires premium vous offre une présence stratégique auprès d'une clientèle ciblée.
        </p>
      </div>

      {/* Brochure Access */}
      <button
        onClick={() => setShowBrochure(true)}
        data-testid="btn-open-brochure"
        className="w-full mb-8 bg-[#F9F6F0] border border-[#E5E5E5] p-6 flex items-center justify-between hover:border-[#B89F7C] transition-colors group"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white flex items-center justify-center">
            <BookOpen size={24} className="text-[#B89F7C]" strokeWidth={1.5} />
          </div>
          <div className="text-left">
            <p className="font-heading text-lg text-[#0A0A0A]">Découvrir notre réseau de diffusion</p>
            <p className="font-body text-sm text-[#525252]">
              Explorez les typologies de lieux, profils de clientèle et positionnement
            </p>
          </div>
        </div>
        <ArrowRight size={20} className="text-[#B89F7C] group-hover:translate-x-1 transition-transform" />
      </button>

      {/* Distribution Tiers */}
      <div className="grid lg:grid-cols-3 gap-6 mb-10">
        {tiers.map((tier) => {
          const Icon = tierIcons[tier.id];
          const isSelected = selectedDistributionTier === tier.id;
          
          return (
            <button
              key={tier.id}
              onClick={() => setDistributionTier(tier.id)}
              data-testid={`tier-${tier.id}`}
              className={`relative p-8 text-left border transition-all duration-300 flex flex-col h-full ${
                isSelected
                  ? "bg-[#0A0A0A] border-[#0A0A0A] text-white"
                  : "bg-white border-[#E5E5E5] hover:border-[#B89F7C]"
              }`}
            >
              {/* Recommended Badge */}
              {tier.recommended && (
                <div className={`absolute -top-3 left-6 flex items-center gap-1 px-3 py-1 text-xs tracking-wider uppercase ${
                  isSelected ? "bg-[#B89F7C] text-white" : "bg-[#B89F7C] text-white"
                }`}>
                  <Star size={12} fill="currentColor" />
                  Le plus choisi
                </div>
              )}

              {/* Icon & Title */}
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 flex items-center justify-center ${
                  isSelected ? "bg-[#B89F7C]" : "bg-[#F9F6F0]"
                }`}>
                  <Icon size={20} className={isSelected ? "text-white" : "text-[#B89F7C]"} strokeWidth={1.5} />
                </div>
                <div>
                  <p className={`font-body text-xs tracking-[0.1em] uppercase ${
                    isSelected ? "text-[#B89F7C]" : "text-[#525252]"
                  }`}>
                    {tier.subtitle}
                  </p>
                  <h3 className={`font-heading text-xl ${isSelected ? "text-white" : "text-[#0A0A0A]"}`}>
                    {tier.name}
                  </h3>
                </div>
              </div>

              {/* Hook */}
              <p className={`font-heading text-sm italic mb-3 ${isSelected ? "text-[#B89F7C]" : "text-[#B89F7C]"}`}>
                {tier.hook}
              </p>

              {/* Description */}
              <p className={`font-body text-sm mb-4 leading-relaxed ${isSelected ? "text-white/80" : "text-[#525252]"}`}>
                {tier.description}
              </p>

              {/* Features */}
              <ul className="space-y-2 mb-6 flex-1">
                {tier.features.map((feature) => (
                  <li key={feature} className={`flex items-start gap-2 font-body text-sm ${
                    isSelected ? "text-white/80" : "text-[#525252]"
                  }`}>
                    <Check size={16} className={`shrink-0 mt-0.5 ${
                      isSelected ? "text-[#B89F7C]" : "text-[#B89F7C]"
                    }`} />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* Audience Quality Indicator */}
              <div className={`p-4 mb-4 ${isSelected ? "bg-white/10" : "bg-[#F9F6F0]"}`}>
                <p className={`font-body text-sm italic ${isSelected ? "text-white/80" : "text-[#B89F7C]"}`}>
                  {tier.microLine}
                </p>
              </div>

              {/* Price */}
              <div className={`pt-4 border-t ${isSelected ? "border-white/20" : "border-[#E5E5E5]"}`}>
                {tier.price > 0 ? (
                  <p className={`font-body text-sm ${isSelected ? "text-white" : "text-[#0A0A0A]"}`}>
                    <span className="font-heading text-xl">+{tier.price}€</span> HT/mois
                  </p>
                ) : (
                  <p className={`font-body text-sm ${isSelected ? "text-[#B89F7C]" : "text-[#B89F7C]"}`}>
                    Inclus dans votre forfait
                  </p>
                )}
              </div>

              {/* Selection Indicator */}
              {isSelected && (
                <div className="absolute top-4 right-4 w-6 h-6 bg-[#B89F7C] flex items-center justify-center">
                  <Check size={14} className="text-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Positioning Note */}
      <div className="bg-white border border-[#E5E5E5] p-6 mb-8">
        <p className="font-body text-sm text-[#525252]">
          <span className="font-medium text-[#0A0A0A]">Notre promesse :</span> Quel que soit le niveau choisi, 
          votre marque sera présente dans des environnements soigneusement sélectionnés, 
          en cohérence avec l'univers des maisons de parfums.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-[#E5E5E5]">
        <button
          onClick={prevStep}
          data-testid="btn-prev-step"
          className="flex items-center gap-2 font-body text-sm text-[#525252] hover:text-[#0A0A0A] transition-colors"
        >
          <ArrowLeft size={16} />
          Retour
        </button>
        <button
          onClick={nextStep}
          disabled={!selectedDistributionTier}
          data-testid="btn-next-step"
          className="bg-[#0A0A0A] text-white hover:bg-[#B89F7C] transition-colors duration-300 px-8 py-3 text-sm tracking-widest uppercase font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          Continuer
          <ArrowRight size={16} />
        </button>
      </div>

      {/* Brochure Modal */}
      <BrochureModal isOpen={showBrochure} onClose={() => setShowBrochure(false)} />
    </div>
  );
};

export default StepDistribution;
